import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '../lib/api';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { toast } from './ui/sonner';
import {
  User,
  Building2,
  Settings,
  Lock,
  X,
  Camera,
  Upload,
  Eye,
  EyeOff,
  Globe,
  Clock,
  CreditCard,
  Bell,
  Save,
  ChevronRight,
  Check,
  Loader2,
  Zap,
  Crown,
  TrendingUp,
  AlertTriangle,
} from 'lucide-react';
import { usePlan } from '../context/PlanContext';
import { UpgradeModal } from './UpgradeModal';

/* ─── tiny helpers ─────────────────────────────────── */
const fileToBase64 = file =>
  new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result);
    reader.onerror = rej;
    reader.readAsDataURL(file);
  });

const CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'AED', 'SAR', 'CAD', 'AUD', 'SGD', 'JPY'];
const TIMEZONES = [
  'UTC',
  'Asia/Kolkata',
  'America/New_York',
  'America/Los_Angeles',
  'America/Chicago',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Dubai',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Africa/Johannesburg',
  'America/Sao_Paulo',
];
const DURATIONS = ['1 Month', '3 Months', '6 Months', '1 Year'];

/* ─── reusable styled input ──────────────────────────*/
function Field({ label, children, hint }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'hsl(var(--muted-foreground))',
          textTransform: 'uppercase',
          letterSpacing: '0.8px',
        }}
      >
        {label}
      </label>
      {children}
      {hint && <span style={{ fontSize: 11, color: 'hsl(var(--muted-foreground))' }}>{hint}</span>}
    </div>
  );
}

function Input({ style, ...props }) {
  return (
    <input
      style={{
        width: '100%',
        padding: '10px 14px',
        borderRadius: 10,
        border: '1.5px solid hsl(var(--border))',
        background: 'hsl(var(--background))',
        color: 'hsl(var(--foreground))',
        fontSize: 14,
        outline: 'none',
        transition: 'border-color .2s',
        boxSizing: 'border-box',
        ...style,
      }}
      onFocus={e => (e.target.style.borderColor = 'hsl(221,83%,53%)')}
      onBlur={e => (e.target.style.borderColor = 'hsl(var(--border))')}
      {...props}
    />
  );
}

function Select({ children, style, ...props }) {
  return (
    <select
      style={{
        width: '100%',
        padding: '10px 14px',
        borderRadius: 10,
        border: '1.5px solid hsl(var(--border))',
        background: 'hsl(var(--background))',
        color: 'hsl(var(--foreground))',
        fontSize: 14,
        outline: 'none',
        cursor: 'pointer',
        boxSizing: 'border-box',
        ...style,
      }}
      {...props}
    >
      {children}
    </select>
  );
}

/* ─── circular photo uploader ────────────────────────*/
function PhotoUploader({ value, onChange, size = 100, label = 'Upload Photo' }) {
  const inputRef = useRef();

  const handleFile = async e => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Image must be under 3 MB');
      return;
    }
    const b64 = await fileToBase64(file);
    onChange(b64);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
      <div
        onClick={() => inputRef.current.click()}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: value
            ? 'transparent'
            : 'linear-gradient(135deg,hsl(221,83%,53%),hsl(260,70%,55%))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          position: 'relative',
          overflow: 'hidden',
          border: '3px solid hsl(var(--border))',
          transition: 'transform .2s, box-shadow .2s',
          boxShadow: '0 4px 16px rgba(37,99,235,.25)',
          flexShrink: 0,
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'scale(1.04)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(37,99,235,.4)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 16px rgba(37,99,235,.25)';
        }}
      >
        {value ? (
          <img
            src={value}
            alt="avatar"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Camera size={size * 0.28} color="#fff" />
        )}
        {/* hover overlay */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0,
            transition: 'opacity .2s',
            borderRadius: '50%',
          }}
          onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
          onMouseLeave={e => (e.currentTarget.style.opacity = '0')}
        >
          <Camera size={18} color="#fff" />
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFile}
      />
      <button
        type="button"
        onClick={() => inputRef.current.click()}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '7px 16px',
          borderRadius: 8,
          border: '1.5px solid hsl(var(--border))',
          background: 'transparent',
          color: 'hsl(var(--foreground))',
          fontSize: 12,
          cursor: 'pointer',
          fontWeight: 600,
          transition: 'background .15s',
        }}
        onMouseEnter={e => (e.currentTarget.style.background = 'hsl(var(--muted))')}
        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
      >
        <Upload size={13} /> {label}
      </button>
    </div>
  );
}

/* ─── Save button ──────────────────────────────────── */
function SaveButton({ loading, onClick, children = 'Save Changes' }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '11px 28px',
        borderRadius: 10,
        border: 'none',
        background: 'linear-gradient(135deg,hsl(221,83%,53%),hsl(260,70%,55%))',
        color: '#fff',
        fontWeight: 700,
        fontSize: 14,
        cursor: loading ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.75 : 1,
        transition: 'opacity .2s, transform .15s',
        boxShadow: '0 4px 18px rgba(37,99,235,.35)',
      }}
      onMouseEnter={e => {
        if (!loading) {
          e.currentTarget.style.transform = 'translateY(-1px)';
        }
      }}
      onMouseLeave={e => (e.currentTarget.style.transform = 'translateY(0)')}
    >
      {loading ? (
        <Loader2 size={15} style={{ animation: 'spin .8s linear infinite' }} />
      ) : (
        <Save size={15} />
      )}
      {children}
    </button>
  );
}

/* ─── Plan Status Section (inside Profile tab) ───────*/
function UsageBarSmall({ label, used, limit, color }) {
  const pct = limit === Infinity ? 0 : Math.min(100, (used / limit) * 100);
  const near = pct >= 85;
  const barColor = pct >= 100 ? '#ef4444' : near ? '#f59e0b' : color;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'hsl(var(--muted-foreground))' }}>
          {label}
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: barColor }}>
          {used} / {limit === Infinity ? '∞' : limit}
        </span>
      </div>
      <div
        style={{ height: 5, borderRadius: 99, background: 'hsl(var(--muted))', overflow: 'hidden' }}
      >
        <div
          style={{
            height: '100%',
            borderRadius: 99,
            width: `${limit === Infinity ? 0 : pct}%`,
            background: barColor,
            transition: 'width .5s ease',
          }}
        />
      </div>
    </div>
  );
}

function PlanStatusSection() {
  const {
    plan,
    isTrial,
    trialDaysLeft,
    isExpired,
    memberLimit,
    trainerLimit,
    memberCount,
    trainerCount,
    planId,
  } = usePlan();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const urgent = isTrial && trialDaysLeft <= 2;

  return (
    <>
      <div
        style={{
          marginTop: 20,
          background: urgent ? 'rgba(249,115,22,0.05)' : 'hsl(var(--muted))',
          border: `1.5px solid ${urgent ? 'rgba(249,115,22,0.3)' : 'hsl(var(--border))'}`,
          borderRadius: 12,
          padding: '18px 20px',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 9,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: planId === 'pro' ? 'rgba(245,158,11,0.15)' : 'rgba(99,102,241,0.12)',
              }}
            >
              {planId === 'pro' ? (
                <Crown size={16} style={{ color: '#f59e0b' }} />
              ) : (
                <Zap size={16} style={{ color: '#6366f1' }} />
              )}
            </div>
            <div>
              <p
                style={{
                  margin: 0,
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  color: 'hsl(var(--muted-foreground))',
                }}
              >
                Current Plan
              </p>
              <p
                style={{
                  margin: 0,
                  fontSize: 15,
                  fontWeight: 800,
                  color: 'hsl(var(--foreground))',
                }}
              >
                {plan.name}
              </p>
            </div>
          </div>
          {planId !== 'pro' && (
            <button
              onClick={() => setUpgradeOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                padding: '6px 14px',
                borderRadius: 8,
                background: 'linear-gradient(135deg,hsl(221,83%,53%),hsl(260,70%,55%))',
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 3px 12px rgba(99,102,241,0.3)',
              }}
            >
              <TrendingUp size={12} /> Upgrade
            </button>
          )}
        </div>

        {/* Trial countdown */}
        {isTrial && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 12px',
              borderRadius: 9,
              marginBottom: 12,
              background: urgent ? 'rgba(249,115,22,0.1)' : 'rgba(99,102,241,0.08)',
              border: `1px solid ${urgent ? 'rgba(249,115,22,0.2)' : 'rgba(99,102,241,0.15)'}`,
            }}
          >
            {urgent ? (
              <AlertTriangle size={13} style={{ color: '#f97316', flexShrink: 0 }} />
            ) : (
              <Clock size={13} style={{ color: '#6366f1', flexShrink: 0 }} />
            )}
            <span style={{ fontSize: 12, fontWeight: 600, color: urgent ? '#f97316' : '#6366f1' }}>
              {isExpired
                ? '⚠ Trial expired — upgrade to continue.'
                : `Trial ends in ${trialDaysLeft} day${trialDaysLeft !== 1 ? 's' : ''}`}
            </span>
          </div>
        )}

        {/* Usage bars */}
        <UsageBarSmall
          label="Members used"
          used={memberCount}
          limit={memberLimit}
          color="#6366f1"
        />
        <UsageBarSmall
          label="Trainers used"
          used={trainerCount}
          limit={trainerLimit}
          color="#a855f7"
        />

        {planId !== 'pro' && (
          <button
            onClick={() => setUpgradeOpen(true)}
            style={{
              marginTop: 12,
              width: '100%',
              padding: '10px',
              borderRadius: 9,
              border: 'none',
              cursor: 'pointer',
              background: 'linear-gradient(135deg, hsl(221,83%,53%), hsl(260,70%,55%))',
              color: '#fff',
              fontSize: 12,
              fontWeight: 700,
              boxShadow: '0 4px 14px rgba(99,102,241,0.25)',
            }}
          >
            {planId === 'trial' ? 'Upgrade — from $25/mo' : 'Upgrade to Pro — $45/mo'}
          </button>
        )}
      </div>
      <UpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        feature="the Pro plan"
      />
    </>
  );
}

/* ─── password field helper ─── */
const PwdInput = ({ label: lbl, value, onChange, showText, onToggleShow }) => (
  <Field label={lbl}>
    <div style={{ position: 'relative' }}>
      <Input
        type={showText ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="••••••••"
        style={{ paddingRight: 42 }}
      />
      <button
        type="button"
        onClick={onToggleShow}
        style={{
          position: 'absolute',
          right: 12,
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'hsl(var(--muted-foreground))',
        }}
      >
        {showText ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  </Field>
);

/* ─── MAIN MODAL ─────────────────────────────────────*/
export default function ProfileModal({ open, initialTab = 'profile', onClose, onProfileUpdated }) {
  const { theme } = useTheme();
  const { refreshGym } = useAuth();
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Profile tab state
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [profilePhoto, setProfilePhoto] = useState('');

  // Gym info tab state
  const [gymName, setGymName] = useState('');
  const [gymLogo, setGymLogo] = useState('');
  const [gymAddress, setGymAddress] = useState('');
  const [gymCity, setGymCity] = useState('');
  const [gymCountry, setGymCountry] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');

  // Settings tab state
  const [currency, setCurrency] = useState('USD');
  const [timezone, setTimezone] = useState('UTC');
  const [defaultDuration, setDefaultDuration] = useState('1 Month');
  const [notificationsOn, setNotificationsOn] = useState(true);
  // Password change
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showPwd, setShowPwd] = useState({ cur: false, new: false, con: false });
  const [pwdSaving, setPwdSaving] = useState(false);

  // sync tab when prop changes
  useEffect(() => {
    if (open) {
      setTab(initialTab);
    }
  }, [open, initialTab]);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/profile`);
      const p = data.profile || {};
      const s = data.settings || {};
      setOwnerName(p.owner_name || '');
      setOwnerEmail(p.email || '');
      setOwnerPhone(p.phone || '');
      setProfilePhoto(p.profile_photo || '');
      setGymName(p.gym_name || '');
      setGymLogo(p.gym_logo || '');
      setGymAddress(p.address || '');
      setGymCity(p.city || '');
      setGymCountry(p.country || '');
      setContactPhone(p.contact_phone || '');
      setBusinessEmail(p.business_email || '');
      setCurrency(s.currency || 'USD');
      setTimezone(s.timezone || 'UTC');
      setDefaultDuration(s.default_membership_duration || '1 Month');
      setNotificationsOn(s.notifications_enabled !== false);
    } catch {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadProfile();
    }
  }, [open, loadProfile]);

  // Close on Escape
  useEffect(() => {
    const handler = e => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (open) {
      window.addEventListener('keydown', handler);
    }
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  /* ── save handlers ── */
  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/profile`, {
        owner_name: ownerName,
        phone: ownerPhone,
        profile_photo: profilePhoto || null,
      });
      toast.success('Profile updated successfully! ✓');
      onProfileUpdated?.(data.profile);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const saveGymInfo = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/gym-info`, {
        gym_name: gymName,
        gym_logo: gymLogo || null,
        address: gymAddress,
        city: gymCity,
        country: gymCountry,
        contact_phone: contactPhone,
        business_email: businessEmail,
      });
      toast.success('Gym information saved! ✓');
      onProfileUpdated?.(data.profile);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save gym info');
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/account-settings`, {
        currency,
        timezone,
        default_membership_duration: defaultDuration,
        notifications_enabled: notificationsOn,
      });
      toast.success('Settings saved! ✓');
      // Refresh AuthContext so currencySymbol updates everywhere instantly
      await refreshGym();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (!currentPwd || !newPwd || !confirmPwd) {
      toast.error('Please fill all password fields');
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPwd.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setPwdSaving(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: currentPwd,
        new_password: newPwd,
      });
      toast.success('Password changed successfully! ✓');
      setCurrentPwd('');
      setNewPwd('');
      setConfirmPwd('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwdSaving(false);
    }
  };

  /* ── tab definitions ── */
  const tabs = [
    { id: 'profile', label: 'My Profile', icon: User },
    { id: 'gym-info', label: 'Gym Information', icon: Building2 },
    { id: 'settings', label: 'Account Settings', icon: Settings },
  ];

  const isDark = theme === 'dark';

  /* ── card style shared ── */
  const card = {
    background: isDark ? 'hsl(222 47% 10%)' : 'hsl(0 0% 98%)',
    border: `1px solid hsl(var(--border))`,
    borderRadius: 12,
    padding: 24,
    marginBottom: 20,
  };

  return (
    <>
      {/* keyframe for spinner */}
      <style>{`
        @keyframes spin { to { transform:rotate(360deg); } }
        @keyframes modalFade { from { opacity:0; transform:scale(.96) translateY(12px); } to { opacity:1; transform:scale(1) translateY(0); } }
        .profile-tab-btn { transition: all .18s; }
        .profile-tab-btn:hover { background: hsl(var(--muted)) !important; }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1000,
          background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(4px)',
        }}
      />

      {/* Modal panel */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 820,
            maxHeight: '92vh',
            background: isDark ? 'hsl(222 47% 7%)' : '#fff',
            borderRadius: 20,
            boxShadow: '0 32px 96px rgba(0,0,0,0.35)',
            display: 'flex',
            flexDirection: 'column',
            animation: 'modalFade .25s ease',
            pointerEvents: 'all',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '20px 28px',
              borderBottom: `1px solid hsl(var(--border))`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: isDark ? 'hsl(222 47% 9%)' : 'hsl(220 20% 97%)',
              flexShrink: 0,
            }}
          >
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: 20,
                  fontWeight: 800,
                  color: 'hsl(var(--foreground))',
                  letterSpacing: '-0.3px',
                }}
              >
                Profile & Settings
              </h2>
              <p
                style={{
                  margin: 0,
                  fontSize: 12,
                  color: 'hsl(var(--muted-foreground))',
                  marginTop: 2,
                }}
              >
                Manage your account, gym identity, and preferences
              </p>
            </div>
            <button
              onClick={onClose}
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                border: 'none',
                background: 'hsl(var(--muted))',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'hsl(var(--foreground))',
                transition: 'background .15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'hsl(var(--destructive))')}
              onMouseLeave={e => (e.currentTarget.style.background = 'hsl(var(--muted))')}
            >
              <X size={16} />
            </button>
          </div>

          {/* Body: sidebar + content */}
          <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
            {/* Left sidebar tabs */}
            <nav
              style={{
                width: 196,
                padding: '16px 10px',
                borderRight: `1px solid hsl(var(--border))`,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                background: isDark ? 'hsl(222 47% 8%)' : 'hsl(220 20% 97%)',
                flexShrink: 0,
              }}
            >
              {tabs.map(t => {
                const Icon = t.icon;
                const active = tab === t.id;
                return (
                  <button
                    key={t.id}
                    className="profile-tab-btn"
                    onClick={() => setTab(t.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '11px 14px',
                      borderRadius: 10,
                      border: 'none',
                      background: active
                        ? 'linear-gradient(135deg, hsl(221,83%,53%), hsl(260,70%,55%))'
                        : 'transparent',
                      color: active ? '#fff' : 'hsl(var(--foreground))',
                      fontWeight: active ? 700 : 500,
                      fontSize: 13,
                      cursor: 'pointer',
                      textAlign: 'left',
                      boxShadow: active ? '0 4px 14px rgba(37,99,235,.35)' : 'none',
                      transition: 'all .18s',
                    }}
                  >
                    <Icon size={15} style={{ flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>{t.label}</span>
                    {active && <ChevronRight size={12} style={{ opacity: 0.7 }} />}
                  </button>
                );
              })}
            </nav>

            {/* Right content */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '24px 28px',
              }}
            >
              {loading ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 200,
                  }}
                >
                  <Loader2
                    size={28}
                    style={{ animation: 'spin .8s linear infinite', color: 'hsl(221,83%,53%)' }}
                  />
                </div>
              ) : (
                /* ── MY PROFILE ── */
                tab === 'profile' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {/* Photo upload */}
                    <div
                      style={{
                        ...card,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 28,
                        flexWrap: 'wrap',
                      }}
                    >
                      <PhotoUploader
                        value={profilePhoto}
                        onChange={setProfilePhoto}
                        size={110}
                        label="Upload Photo"
                      />
                      <div style={{ flex: 1, minWidth: 160 }}>
                        <h3
                          style={{
                            margin: 0,
                            fontSize: 16,
                            fontWeight: 700,
                            color: 'hsl(var(--foreground))',
                          }}
                        >
                          {ownerName || 'Gym Owner'}
                        </h3>
                        <p
                          style={{
                            margin: '4px 0 0',
                            fontSize: 13,
                            color: 'hsl(var(--muted-foreground))',
                          }}
                        >
                          {ownerEmail}
                        </p>
                        <span
                          style={{
                            display: 'inline-block',
                            marginTop: 8,
                            padding: '3px 10px',
                            background: 'linear-gradient(135deg,hsl(221,83%,53%),hsl(260,70%,55%))',
                            borderRadius: 20,
                            fontSize: 11,
                            color: '#fff',
                            fontWeight: 700,
                          }}
                        >
                          Gym Owner
                        </span>
                      </div>
                    </div>

                    {/* Form fields */}
                    <div style={{ ...card, marginBottom: 0 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                        <Field label="Owner Full Name">
                          <Input
                            value={ownerName}
                            onChange={e => setOwnerName(e.target.value)}
                            placeholder="John Doe"
                          />
                        </Field>
                        <Field label="Email Address" hint="Email cannot be changed">
                          <Input
                            value={ownerEmail}
                            readOnly
                            style={{ opacity: 0.65, cursor: 'not-allowed' }}
                          />
                        </Field>
                        <Field label="Phone Number">
                          <Input
                            value={ownerPhone}
                            onChange={e => setOwnerPhone(e.target.value)}
                            placeholder="+1 555 000 0000"
                          />
                        </Field>
                      </div>

                      <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
                        <SaveButton loading={saving} onClick={saveProfile} />
                      </div>
                    </div>

                    {/* Plan Status Section */}
                    <PlanStatusSection />
                  </div>
                )
              )}

              {/* ── GYM INFORMATION ── */}
              {!loading && tab === 'gym-info' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {/* Logo upload */}
                  <div
                    style={{
                      ...card,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 28,
                      flexWrap: 'wrap',
                    }}
                  >
                    <PhotoUploader
                      value={gymLogo}
                      onChange={setGymLogo}
                      size={110}
                      label="Upload Gym Logo"
                    />
                    <div style={{ flex: 1, minWidth: 160 }}>
                      <h3
                        style={{
                          margin: 0,
                          fontSize: 16,
                          fontWeight: 700,
                          color: 'hsl(var(--foreground))',
                        }}
                      >
                        {gymName || 'Your Gym'}
                      </h3>
                      <p
                        style={{
                          margin: '4px 0 0',
                          fontSize: 13,
                          color: 'hsl(var(--muted-foreground))',
                        }}
                      >
                        {gymCity || 'City'}
                        {gymCountry ? `, ${gymCountry}` : ''}
                      </p>
                      <p
                        style={{
                          margin: '6px 0 0',
                          fontSize: 12,
                          color: 'hsl(var(--muted-foreground))',
                        }}
                      >
                        Logo will appear on invoices, notifications & reminders
                      </p>
                    </div>
                  </div>

                  {/* Form fields */}
                  <div style={{ ...card, marginBottom: 0 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                      <Field label="Gym Name">
                        <Input
                          value={gymName}
                          onChange={e => setGymName(e.target.value)}
                          placeholder="Fitxero Gym"
                        />
                      </Field>
                      <Field label="Business Email">
                        <Input
                          type="email"
                          value={businessEmail}
                          onChange={e => setBusinessEmail(e.target.value)}
                          placeholder="gym@example.com"
                        />
                      </Field>
                      <Field label="Address">
                        <Input
                          value={gymAddress}
                          onChange={e => setGymAddress(e.target.value)}
                          placeholder="123 Main Street"
                        />
                      </Field>
                      <Field label="City">
                        <Input
                          value={gymCity}
                          onChange={e => setGymCity(e.target.value)}
                          placeholder="New York"
                        />
                      </Field>
                      <Field label="Country">
                        <Input
                          value={gymCountry}
                          onChange={e => setGymCountry(e.target.value)}
                          placeholder="United States"
                        />
                      </Field>
                      <Field label="Contact Phone">
                        <Input
                          value={contactPhone}
                          onChange={e => setContactPhone(e.target.value)}
                          placeholder="+1 555 000 0000"
                        />
                      </Field>
                    </div>

                    <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
                      <SaveButton loading={saving} onClick={saveGymInfo} />
                    </div>
                  </div>
                </div>
              )}

              {/* ── ACCOUNT SETTINGS ── */}
              {!loading && tab === 'settings' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {/* Preferences */}
                  <div style={card}>
                    <h3
                      style={{
                        margin: '0 0 18px',
                        fontSize: 15,
                        fontWeight: 700,
                        color: 'hsl(var(--foreground))',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <Globe size={16} style={{ color: 'hsl(221,83%,53%)' }} /> System Preferences
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                      <Field label="Currency">
                        <Select value={currency} onChange={e => setCurrency(e.target.value)}>
                          {CURRENCIES.map(c => (
                            <option key={c}>{c}</option>
                          ))}
                        </Select>
                      </Field>
                      <Field label="Timezone">
                        <Select value={timezone} onChange={e => setTimezone(e.target.value)}>
                          {TIMEZONES.map(t => (
                            <option key={t}>{t}</option>
                          ))}
                        </Select>
                      </Field>
                      <Field label="Default Membership Duration">
                        <Select
                          value={defaultDuration}
                          onChange={e => setDefaultDuration(e.target.value)}
                        >
                          {DURATIONS.map(d => (
                            <option key={d}>{d}</option>
                          ))}
                        </Select>
                      </Field>

                      {/* Notifications toggle */}
                      <Field label="Notifications">
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            padding: '10px 14px',
                            border: '1.5px solid hsl(var(--border))',
                            borderRadius: 10,
                            cursor: 'pointer',
                          }}
                          onClick={() => setNotificationsOn(v => !v)}
                        >
                          <div
                            style={{
                              width: 42,
                              height: 24,
                              borderRadius: 12,
                              position: 'relative',
                              background: notificationsOn
                                ? 'hsl(221,83%,53%)'
                                : 'hsl(var(--muted))',
                              transition: 'background .2s',
                              flexShrink: 0,
                            }}
                          >
                            <div
                              style={{
                                position: 'absolute',
                                top: 3,
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                background: '#fff',
                                transition: 'left .2s',
                                left: notificationsOn ? 21 : 3,
                                boxShadow: '0 1px 4px rgba(0,0,0,.2)',
                              }}
                            />
                          </div>
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 500,
                              color: 'hsl(var(--foreground))',
                            }}
                          >
                            {notificationsOn ? 'Enabled' : 'Disabled'}
                          </span>
                          {notificationsOn && (
                            <Bell size={13} style={{ color: 'hsl(221,83%,53%)' }} />
                          )}
                        </div>
                      </Field>
                    </div>
                    <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
                      <SaveButton loading={saving} onClick={saveSettings} />
                    </div>
                  </div>

                  {/* Change password */}
                  <div style={{ ...card, marginBottom: 0, borderColor: 'hsl(var(--border))' }}>
                    <h3
                      style={{
                        margin: '0 0 18px',
                        fontSize: 15,
                        fontWeight: 700,
                        color: 'hsl(var(--foreground))',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <Lock size={16} style={{ color: 'hsl(221,83%,53%)' }} /> Change Password
                    </h3>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr',
                        gap: 14,
                        maxWidth: 400,
                      }}
                    >
                      <PwdInput
                        label="Current Password"
                        value={currentPwd}
                        onChange={setCurrentPwd}
                        showText={showPwd.cur}
                        onToggleShow={() => setShowPwd(p => ({ ...p, cur: !p.cur }))}
                      />
                      <PwdInput
                        label="New Password"
                        value={newPwd}
                        onChange={setNewPwd}
                        showText={showPwd.new}
                        onToggleShow={() => setShowPwd(p => ({ ...p, new: !p.new }))}
                      />
                      <PwdInput
                        label="Confirm New Password"
                        value={confirmPwd}
                        onChange={setConfirmPwd}
                        showText={showPwd.con}
                        onToggleShow={() => setShowPwd(p => ({ ...p, con: !p.con }))}
                      />
                    </div>
                    <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-start' }}>
                      <SaveButton loading={pwdSaving} onClick={changePassword}>
                        Change Password
                      </SaveButton>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
